import { getRedirectUri } from '@/common/utils/auth/redirect';
import { getLogoutUri, getPartnerRequest } from '@/common/utils/sso/sso.util';
import { isTimestampOnTime } from '@/common/utils/strings/time.utils';
import { AuthData } from '@/core/domain/auth/models/auth.data';
import { AuthRepository } from '@/core/domain/auth/repositories/auth.repository';
import { AuthApi } from '@/core/domain/auth/services/auth.api';
import { SingleSignOnApi } from '@/core/domain/auth/services/sso.api';
import { LoginUseCaseInput } from '@/core/domain/auth/usecases/login.usecase';
import { LoginExternalUseCaseInput } from '@/core/domain/auth/usecases/loginexternal.usecase';
import { ResponsePostLogin } from '@/core/domain/auth/usecases/post-login.usecase';
import { GtmError } from '@/core/domain/gtm/models/gtm-error.models';
import { SetDataLayerParamsUseCase } from '@/core/domain/gtm/usecases/setDataLayer.usecase';
import * as actions from '@/data/auth/store/auth.actions';
import { updateLoadingUser } from '@/data/auth/store/loading-user.actions';
import * as actionsEtoken from '@/data/auth/store/vivaUserEtoken/etoken.actions';
import { openModalError } from '@/data/modal-error/store/modal-error.actions';
import { LOCAL_STORAGE_VIVA_USER_ETOKEN_STATE_KEY } from '@/data/persistence/viva-user-etoken';
import {
    LOCAL_STORAGE_VIVA_USER_TOKEN_EXPIRES_STATE_KEY,
    LOCAL_STORAGE_VIVA_USER_TOKEN_STATE_KEY,
} from '@/data/persistence/viva-user-token-expires.reducer';
import { environment } from '@/env/environment';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { Observable, catchError, first, of, switchMap, throwError } from 'rxjs';
import { AuthMapper } from '../mappers/auth.mapper';
import { LoginMapper } from '../services/ezy/mappers/login.mapper';
import { UserDetails } from '../services/ezy/responses/interfaces/user-details.interface';
import { LoginExternalResponse } from '../services/ezy/responses/login-external.response';
import { RestoreResponse } from '../services/ezy/responses/restore.response';
import { selectAuthValue } from '../store/auth.selectors';
import { updateCatchErrorRestore } from '../store/catchErrorRestore/error-restore.actions';
import { updateFromSSOData } from '../store/from-sso.actions';
import { selectFromSSOState } from '../store/from-sso.selector';
import { AuthUserData } from '../store/models/auth.store';

export class AuthImplRepository extends AuthRepository {
    constructor(
        private readonly store: Store,
        private readonly service: AuthApi,
        private readonly serviceSSO: SingleSignOnApi,
        private readonly router: Router,
    ) {
        super();
    }

    private readonly gtmDataLayer = inject(SetDataLayerParamsUseCase);
    private readonly translate = inject(TranslateService);

    override checkLoginExternalStatus(): Observable<boolean> {
        this.store.dispatch(updateLoadingUser({ value: false }));
        this.store.dispatch(updateCatchErrorRestore({ value: false }));
        const vivaUserToken = localStorage.getItem(
            LOCAL_STORAGE_VIVA_USER_TOKEN_STATE_KEY,
        );

        if (vivaUserToken === null) {
            return this.store.select(selectFromSSOState).pipe(
                switchMap((isSSO) => {
                    this.store.dispatch(updateFromSSOData({ value: false }));
                    if (isSSO) {
                        return of(false);
                    }
                    return this.store.select(selectAuthValue).pipe(
                        first(),
                        switchMap((auth) => {
                            if (auth.isLogged) {
                                this.store.dispatch(actions.resetToken());
                                this.router.navigate(['/']);
                            }
                            return of(false);
                        }),
                    );
                }),
            );
        }

        //Has vivaUserToken, continue to check if is necesary recovery login
        return this.store.select(selectAuthValue).pipe(
            first(),
            switchMap((auth) => {
                //Check if vg-data is logged make a new request to restore
                if (!auth.isLogged) {
                    return this.evaluateLoginMechanism(vivaUserToken);
                }

                // Return AuthData if vg-data is logged
                return of(auth);
            }),
            switchMap(
                (response: LoginExternalResponse | AuthUserData | null) => {
                    // Here the response is null because the restore request failed or login request failed
                    if (response === null) {
                        return of(null);
                    }

                    // When response is a response from restore request just save the tokens
                    if (this.isLoginExternalResponse(response)) {
                        const tokens = new LoginMapper().mapFrom(response.data);
                        this.store.dispatch(actions.updateToken({ tokens }));
                        return of(true);
                    }

                    const vivaUserTokenExpires = localStorage.getItem(
                        LOCAL_STORAGE_VIVA_USER_TOKEN_EXPIRES_STATE_KEY,
                    );

                    // If the response is AuthUserData and checks the viva token expiration
                    if (
                        this.isAuthUserData(response) &&
                        response.authToken !== vivaUserToken &&
                        vivaUserTokenExpires
                    ) {
                        const vivaUserTokenExpiresDate =
                            DateTime.fromISO(vivaUserTokenExpires);

                        const vgUserTokenExpiresDate = DateTime.fromMillis(
                            response.expiration,
                        );

                        // Check expiration
                        if (
                            vivaUserTokenExpiresDate !== vgUserTokenExpiresDate
                        ) {
                            const vivaUserEtoken = localStorage.getItem(
                                LOCAL_STORAGE_VIVA_USER_ETOKEN_STATE_KEY,
                            );

                            // If viva-user-etoken exists save tokens from this token
                            if (vivaUserEtoken) {
                                this.sessionFromVivaEtoken(
                                    response,
                                ).subscribe();

                                return of(null);
                            }
                        }
                        return this.evaluateLoginMechanism(vivaUserToken);
                    }

                    return of(null);
                },
            ),
            switchMap((response: LoginExternalResponse | null | boolean) => {
                // Here the response is null because the restore request failed
                // or login request failed or success or vg user token is after
                // or the token has been saved from viva-user-etoken
                if (response === null) {
                    this.store.dispatch(updateLoadingUser({ value: false }));
                    return of(false);
                }

                if (this.isLoginExternalResponse(response)) {
                    const tokens = new LoginMapper().mapFrom(response.data);
                    this.store.dispatch(actions.updateToken({ tokens }));
                    this.store.dispatch(
                        actionsEtoken.updateEtoken({
                            etoken: response.data.authTokenExternal,
                        }),
                    );
                }
                this.store.dispatch(updateLoadingUser({ value: false }));
                return of(true);
            }),
        );
    }

    private openLoginModalError(): void {
        this.store.dispatch(updateCatchErrorRestore({ value: true }));
        this.store.dispatch(
            openModalError({
                data: {
                    isOpen: true,
                    description: '',
                    error: '',
                    redirectSSO: true,
                },
            }),
        );
    }

    private evaluateLoginMechanism(
        vivaUserToken: string,
    ): Observable<LoginExternalResponse | null> {
        this.store.dispatch(updateLoadingUser({ value: true }));

        return this.service.restore(vivaUserToken).pipe(
            catchError((data) => {
                this.openLoginModalError();
                this.gtmError(data.type, data.code, data.message);
                this.store.dispatch(updateLoadingUser({ value: false }));
                return of(null);
            }),
            switchMap((restoreResponse: RestoreResponse | null) => {
                if (restoreResponse === null) {
                    this.store.dispatch(updateLoadingUser({ value: false }));
                    return of(null);
                }

                if (
                    typeof restoreResponse?.data?.authTokenExternal === 'string'
                ) {
                    this.store.dispatch(updateLoadingUser({ value: false }));
                    this.store.dispatch(
                        actionsEtoken.updateEtoken({
                            etoken: restoreResponse.data.authTokenExternal,
                        }),
                    );
                    return of(restoreResponse as LoginExternalResponse);
                }

                const authToken = restoreResponse?.data?.authToken;

                return this.serviceSSO.loginExternal(authToken).pipe(
                    catchError((data) => {
                        this.openLoginModalError();
                        this.gtmError(data.type, data.code, data.message);
                        this.store.dispatch(
                            updateLoadingUser({ value: false }),
                        );
                        return of(null);
                    }),
                    switchMap((response) => {
                        if (response != null) {
                            response.data.providerType =
                                environment.providerType;
                            this.store.dispatch(
                                actionsEtoken.updateEtoken({
                                    etoken: response.data.authTokenExternal,
                                }),
                            );
                        }
                        this.store.dispatch(
                            updateLoadingUser({ value: false }),
                        );
                        return of(response);
                    }),
                );
            }),
        );
    }

    override loginExternal(
        input: LoginExternalUseCaseInput,
    ): Observable<LoginExternalResponse> {
        return this.serviceSSO.loginExternal(input.token);
    }

    private isLoginExternalResponse(
        response: any,
    ): response is LoginExternalResponse {
        return response && typeof response?.data?.authToken === 'string';
    }

    private isAuthUserData(response: any): response is AuthUserData {
        return response && typeof response.expiration === 'number';
    }

    override login(params?: LoginUseCaseInput): Observable<void> {
        const redirectUri = getRedirectUri(params?.path);
        const partnerRqst = getPartnerRequest(this.store, { redirectUri });

        window.location.href = partnerRqst;
        return of();
    }

    override postLogin(input: AuthData): Observable<ResponsePostLogin> {
        const authLoginInput = new AuthMapper().mapFrom(input);
        authLoginInput.providerType = 'SsoAuthCode';
        this.store.dispatch(updateLoadingUser({ value: true }));
        return this.service.login(authLoginInput).pipe(
            switchMap((response) => {
                const tokens = new LoginMapper().mapFrom(response.data);
                this.store.dispatch(updateLoadingUser({ value: false }));
                this.store.dispatch(actions.updateToken({ tokens }));
                return of({ type: response.type, action: response.action });
            }),
            catchError((error) => {
                this.openLoginModalError();
                this.gtmError(error.type, error.code, error.message);
                this.store.dispatch(updateLoadingUser({ value: false }));
                return throwError(() => error);
            }),
        );
    }

    override checkLogin(): Observable<AuthUserData | null> {
        return this.store.select(selectAuthValue).pipe(
            first(),
            switchMap((auth) => {
                if (!auth.isLogged) {
                    throw new Error('User not logged');
                }
                return of(auth);
            }),
            switchMap((auth) => {
                if (auth.isLogged && !isTimestampOnTime(auth.expiration)) {
                    return this.restore();
                }
                return of(auth);
            }),
            catchError(() => {
                return of(null);
            }),
        );
    }

    override restore(): Observable<AuthUserData | null> {
        return this.store.select(selectAuthValue).pipe(
            first(),
            switchMap((auth) => {
                const vivaUserTokenExpires = localStorage.getItem(
                    LOCAL_STORAGE_VIVA_USER_TOKEN_EXPIRES_STATE_KEY,
                );

                if (!(typeof vivaUserTokenExpires === 'string')) {
                    return this.service.restore(auth.authToken);
                }

                const vivaUserTokenExpiresDate =
                    DateTime.fromISO(vivaUserTokenExpires);

                const vgUserTokenExpiresDate = DateTime.fromMillis(
                    auth.expiration,
                );

                const vivaUserEtoken = localStorage.getItem(
                    LOCAL_STORAGE_VIVA_USER_ETOKEN_STATE_KEY,
                );

                if (vivaUserTokenExpiresDate > vgUserTokenExpiresDate) {
                    if (vivaUserEtoken) {
                        return this.sessionFromVivaEtoken(auth);
                    }
                }

                return this.service.restore(auth.authToken);
            }),
            switchMap((response) => {
                if (this.isLoginExternalResponse(response)) {
                    const tokens = new LoginMapper().mapFrom(response.data);
                    this.store.dispatch(actions.updateToken({ tokens }));
                    return of(tokens);
                }

                if (this.isAuthUserData(response)) {
                    return of(response);
                }

                return of(null);
            }),
            catchError((error) => {
                console.log('==='.repeat(10));
                console.log('/restore');
                console.log(error);

                this.store.dispatch(actions.resetToken());
                this.login().subscribe();
                return throwError(() => error);
            }),
        );
    }

    override logout(): Observable<void | null> {
        return this.store.select(selectAuthValue).pipe(
            first(),
            switchMap((auth) => {
                this.store.dispatch(actions.resetToken());

                if (auth.providerType == environment.providerType) {
                    this.router.navigate(['/']);
                    return of(null);
                }

                const logoutUri = getLogoutUri();
                window.location.href = logoutUri;
                return of();
            }),
        );
    }

    private sessionFromVivaEtoken(
        auth: AuthUserData,
    ): Observable<AuthUserData> {
        const vivaUserEtoken = localStorage.getItem(
            LOCAL_STORAGE_VIVA_USER_ETOKEN_STATE_KEY,
        ) as 'string';

        const vivaUserTokenExpires = localStorage.getItem(
            LOCAL_STORAGE_VIVA_USER_TOKEN_EXPIRES_STATE_KEY,
        ) as 'string';

        const vivaUserTokenExpiresDate = new Date(
            vivaUserTokenExpires,
        ).getTime();

        const vivaUserToken = localStorage.getItem(
            LOCAL_STORAGE_VIVA_USER_TOKEN_STATE_KEY,
        ) as 'string';

        return this.serviceSSO.userInfo(vivaUserEtoken).pipe(
            switchMap((responseUser) => {
                const details: UserDetails = {
                    firstName: responseUser.first,
                    lastName: responseUser.last,
                    email: responseUser.email,
                    gender: responseUser.customer_programs.personalDetails
                        .gender,
                    customerNumber: responseUser.sub,
                };
                const tokens: AuthUserData = {
                    ...auth,
                    accessToken: vivaUserEtoken,
                    authToken: vivaUserToken,
                    expiration: vivaUserTokenExpiresDate,
                    details: details,
                };

                this.store.dispatch(actions.updateToken({ tokens }));
                return of(tokens);
            }),
        );
    }

    private gtmError(type: string, code: string, message: string) {
        const translatedCode = this.translate.instant(
            'vg_sso_vg_modal_wrapper_p_descripcion',
        );
        this.gtmDataLayer.execute<GtmError>({
            event: 'ev_message',
            message: {
                type: `${type} | ${translatedCode}`,
                code: code,
                message: message,
            },
        });
    }
}
